// WebAuthn (O100) côté navigateur, sans dépendance : encodeur base64url, conversion des options JSON de l'API en
// options du navigateur, gestes (création, assertion), conversion de la réponse au format JSON de l'API et traduction
// en français des erreurs du navigateur. Rien n'est gardé ici : ni défi, ni assertion, ni réponse (ils vivent le temps de
// l'appel, dans des variables locales, et ne sont écrits ni dans le stockage du navigateur ni dans un cache).
import type { AssertionWebAuthn, CreationWebAuthn } from '../api/types';
import { TEXTE_APPLICATIONS_HUMAINES_NON_DECLAREES, TEXTE_APPLICATION_NON_INTERACTIVE } from '../api/client';

// ---------------------------------------------------------------- base64url (RFC 4648 § 5, sans remplissage)

export function versBase64url(octets: ArrayBuffer | Uint8Array): string {
  const u = octets instanceof Uint8Array ? octets : new Uint8Array(octets);
  let binaire = '';
  for (let i = 0; i < u.length; i += 0x8000) binaire += String.fromCharCode(...u.subarray(i, i + 0x8000));
  return btoa(binaire).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function depuisBase64url(s: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]*$/.test(s)) throw new Error('valeur base64url invalide');
  const b = s.replace(/-/g, '+').replace(/_/g, '/');
  const binaire = atob(b + '='.repeat((4 - (b.length % 4)) % 4));
  const u = new Uint8Array(new ArrayBuffer(binaire.length));
  for (let i = 0; i < binaire.length; i++) u[i] = binaire.charCodeAt(i);
  return u;
}

// ---------------------------------------------------------------- disponibilité

/** Raison pour laquelle le navigateur ne peut pas faire de geste WebAuthn, ou null s'il le peut. Aucun contournement. */
export function indisponibiliteNavigateur(): string | null {
  if (typeof window !== 'undefined' && window.isSecureContext === false)
    return 'Ce navigateur n’autorise WebAuthn que sur une connexion sécurisée (HTTPS, ou localhost) : ouvrez Ekera à son adresse sécurisée.';
  const c = typeof navigator !== 'undefined' ? navigator.credentials : undefined;
  if (!c || typeof c.get !== 'function' || typeof c.create !== 'function')
    return 'Ce navigateur ne sait pas faire de geste WebAuthn (clé de sécurité) : utilisez une version récente de Chrome, Edge, Firefox ou Safari. Aucune validation n’est possible sans ce geste.';
  return null;
}

// ---------------------------------------------------------------- options JSON -> options du navigateur

type Dict = Record<string, unknown>;
interface DescripteurJson {
  id: string;
  [k: string]: unknown;
}

const dict = (v: unknown): Dict => (v && typeof v === 'object' ? (v as Dict) : {});
const descripteurs = (v: unknown): PublicKeyCredentialDescriptor[] =>
  Array.isArray(v)
    ? v.map((d: DescripteurJson) => ({ ...d, type: 'public-key', id: depuisBase64url(String(d.id)) }) as PublicKeyCredentialDescriptor)
    : [];

/** PublicKeyCredentialRequestOptionsJSON -> options de navigator.credentials.get. */
export function optionsDemande(json: Dict): PublicKeyCredentialRequestOptions {
  const natif = (globalThis as { PublicKeyCredential?: { parseRequestOptionsFromJSON?: (o: unknown) => PublicKeyCredentialRequestOptions } })
    .PublicKeyCredential;
  if (natif && typeof natif.parseRequestOptionsFromJSON === 'function') return natif.parseRequestOptionsFromJSON(json);
  const o: Dict = { ...json, challenge: depuisBase64url(String(json.challenge)) };
  if ('allowCredentials' in json) o.allowCredentials = descripteurs(json.allowCredentials);
  return o as unknown as PublicKeyCredentialRequestOptions;
}

/** PublicKeyCredentialCreationOptionsJSON -> options de navigator.credentials.create. */
export function optionsCreation(json: Dict): PublicKeyCredentialCreationOptions {
  const natif = (globalThis as { PublicKeyCredential?: { parseCreationOptionsFromJSON?: (o: unknown) => PublicKeyCredentialCreationOptions } })
    .PublicKeyCredential;
  if (natif && typeof natif.parseCreationOptionsFromJSON === 'function') return natif.parseCreationOptionsFromJSON(json);
  const utilisateur = dict(json.user);
  const o: Dict = {
    ...json,
    challenge: depuisBase64url(String(json.challenge)),
    user: { ...utilisateur, id: depuisBase64url(String(utilisateur.id)) },
  };
  if ('excludeCredentials' in json) o.excludeCredentials = descripteurs(json.excludeCredentials);
  return o as unknown as PublicKeyCredentialCreationOptions;
}

// ---------------------------------------------------------------- réponse du navigateur -> JSON de l'API

const octets = (v: unknown): string => versBase64url(v as ArrayBuffer);

/**
 * Réponse d'un geste de création au format de l'API (`CreationWebAuthn`). Les champs sont choisis un à un : le schéma de
 * l'API est fermé (tout champ en plus est refusé), alors que `toJSON()` en ajoute (`publicKey`, `authenticatorData`…).
 */
export function creationVersJson(c: PublicKeyCredential): CreationWebAuthn {
  const brut = typeof c.toJSON === 'function' ? (c.toJSON() as Dict) : null;
  const r = dict(brut ? brut.response : c.response);
  const rep = c.response as AuthenticatorAttestationResponse;
  const transports = brut ? r.transports : typeof rep.getTransports === 'function' ? rep.getTransports() : undefined;
  return {
    id: String(brut ? brut.id : c.id),
    rawId: brut ? String(brut.rawId) : octets(c.rawId),
    type: 'public-key',
    response: {
      clientDataJSON: brut ? String(r.clientDataJSON) : octets(rep.clientDataJSON),
      attestationObject: brut ? String(r.attestationObject) : octets(rep.attestationObject),
      ...(Array.isArray(transports) && transports.length ? { transports: transports.map(String) } : {}),
    },
    clientExtensionResults: dict(brut ? brut.clientExtensionResults : c.getClientExtensionResults?.()),
    ...rattachement(brut ? brut.authenticatorAttachment : c.authenticatorAttachment),
  };
}

/** Réponse d'un geste d'assertion au format de l'API (`AssertionWebAuthn`), champ par champ. */
export function assertionVersJson(c: PublicKeyCredential): AssertionWebAuthn {
  const brut = typeof c.toJSON === 'function' ? (c.toJSON() as Dict) : null;
  const r = dict(brut ? brut.response : c.response);
  const rep = c.response as AuthenticatorAssertionResponse;
  const identifiant = brut ? r.userHandle : rep.userHandle ? octets(rep.userHandle) : null;
  return {
    id: String(brut ? brut.id : c.id),
    rawId: brut ? String(brut.rawId) : octets(c.rawId),
    type: 'public-key',
    response: {
      clientDataJSON: brut ? String(r.clientDataJSON) : octets(rep.clientDataJSON),
      authenticatorData: brut ? String(r.authenticatorData) : octets(rep.authenticatorData),
      signature: brut ? String(r.signature) : octets(rep.signature),
      userHandle: typeof identifiant === 'string' ? identifiant : null,
    },
    clientExtensionResults: dict(brut ? brut.clientExtensionResults : c.getClientExtensionResults?.()),
    ...rattachement(brut ? brut.authenticatorAttachment : c.authenticatorAttachment),
  };
}

function rattachement(v: unknown): { authenticatorAttachment?: 'platform' | 'cross-platform' } {
  return v === 'platform' || v === 'cross-platform' ? { authenticatorAttachment: v } : {};
}

// ---------------------------------------------------------------- erreurs du navigateur

export type MotifGeste = 'annule' | 'cle_deja_enrolee' | 'non_supporte' | 'securite' | 'indisponible' | 'inattendu';

/** Erreur du navigateur pendant un geste : aucun appel à l'API n'a été fait, rien n'est signé ni enregistré. */
export class ErreurGeste extends Error {
  constructor(
    readonly motif: MotifGeste,
    message: string,
  ) {
    super(message);
    this.name = 'ErreurGeste';
  }
}

export function traduireErreurNavigateur(e: unknown, action: 'creation' | 'assertion'): ErreurGeste {
  if (e instanceof ErreurGeste) return e;
  const nom = e instanceof Error || (typeof DOMException !== 'undefined' && e instanceof DOMException) ? (e as Error).name : '';
  switch (nom) {
    case 'NotAllowedError':
      return new ErreurGeste(
        'annule',
        'Geste annulé, refusé ou trop long (le navigateur attend 60 secondes au plus). Rien n’est signé ni enregistré : vous pouvez recommencer.',
      );
    case 'AbortError':
      return new ErreurGeste('annule', 'Geste interrompu. Rien n’est signé ni enregistré : vous pouvez recommencer.');
    case 'InvalidStateError':
      return new ErreurGeste(
        'cle_deja_enrolee',
        action === 'creation'
          ? 'Cette clé de sécurité est déjà enregistrée pour votre compte : utilisez-en une autre, ou servez-vous de celle-ci pour valider.'
          : 'Cette clé de sécurité ne peut pas servir ici : essayez-en une autre, ou débranchez-la puis rebranchez-la.',
      );
    case 'NotSupportedError':
      return new ErreurGeste(
        'non_supporte',
        'Ce navigateur ou cette clé ne prend pas en charge ce qu’Ekera exige (clé de sécurité certifiée, avec vérification de la personne) : essayez une autre clé ou un autre navigateur.',
      );
    case 'SecurityError':
      return new ErreurGeste(
        'securite',
        'Le navigateur refuse le geste pour cette adresse (nom de domaine ou connexion non sécurisée). Prévenez votre administrateur : la configuration d’Ekera ne correspond pas à l’adresse utilisée.',
      );
    case 'ConstraintError':
      return new ErreurGeste(
        'non_supporte',
        'Aucune clé disponible ne remplit les conditions exigées (vérification de la personne par code ou empreinte) : configurez le code de votre clé, ou essayez-en une autre.',
      );
    default:
      return new ErreurGeste('inattendu', 'Le navigateur n’a pas pu faire le geste. Rien n’est signé ni enregistré : vous pouvez recommencer.');
  }
}

// ---------------------------------------------------------------- gestes

/** Geste de création (enrôlement) : la personne touche sa clé. Rend la réponse au format de l'API, jamais gardée. */
export async function gesteCreation(options: Dict): Promise<CreationWebAuthn> {
  const indispo = indisponibiliteNavigateur();
  if (indispo) throw new ErreurGeste('indisponible', indispo);
  try {
    const c = await navigator.credentials.create({ publicKey: optionsCreation(options) });
    if (!c) throw new ErreurGeste('annule', 'Aucune clé n’a été enregistrée. Vous pouvez recommencer.');
    return creationVersJson(c as PublicKeyCredential);
  } catch (e) {
    throw traduireErreurNavigateur(e, 'creation');
  }
}

/** Geste d'assertion (validation, approbation, ajout) : la personne touche sa clé. userVerification vient de l'API. */
export async function gesteAssertion(options: Dict): Promise<AssertionWebAuthn> {
  const indispo = indisponibiliteNavigateur();
  if (indispo) throw new ErreurGeste('indisponible', indispo);
  try {
    const c = await navigator.credentials.get({ publicKey: optionsDemande(options) });
    if (!c) throw new ErreurGeste('annule', 'Aucun geste n’a été reçu. Rien n’est signé : vous pouvez recommencer.');
    return assertionVersJson(c as PublicKeyCredential);
  } catch (e) {
    throw traduireErreurNavigateur(e, 'assertion');
  }
}

// ---------------------------------------------------------------- libellés

export const LIBELLE_ETAT_CLE: Record<string, string> = {
  en_attente: 'En attente',
  active: 'Active',
  refusee: 'Refusée',
  revoquee: 'Révoquée',
  expiree: 'Expirée',
  invalide: 'Inutilisable : à réenrôler',
};

export const LIBELLE_VOIE_CLE: Record<string, string> = {
  approbation: 'approuvée par un administrateur',
  ajout: 'ajoutée avec une clé déjà active',
  amorcage: 'code d’amorçage',
};

/** État affiché d'une clé : « en attente » devient « en délai de sécurité » quand l'activation est datée. */
export function etatAffiche(etat: string, activeLe: string | null | undefined, voie?: string | null): string {
  if (etat === 'en_attente' && activeLe) return voie === 'ajout' ? 'Ajoutée, en délai de sécurité' : 'Approuvée, en délai de sécurité';
  return LIBELLE_ETAT_CLE[etat] ?? etat;
}

/**
 * Une clé `invalide` (O104) a des colonnes d'état sans le sceau de l'application : elle est inutilisable, signalée aux
 * administrateurs, et ne se « répare » pas ; la personne en enrôle une autre. Jamais proposée à la validation.
 */
export function cleInutilisable(etat: string): boolean {
  return etat === 'invalide';
}

/** Une clé ajoutée avec une clé active est en carence : elle ne vaudra qu'à `active_le`, et se révoque d'ici là sans geste. */
export function cleEnCarence(c: { etat: string; active_le?: string | null }): boolean {
  return c.etat === 'en_attente' && !!c.active_le;
}

/** Forme « XXXX-XXXX » du code de vérification (Crockford base32) : majuscules, tiret au milieu. */
export function normaliserCodeVerification(saisie: string): string {
  const s = saisie.toUpperCase().replace(/[^0-9A-Z]/g, '');
  return s.length > 4 ? `${s.slice(0, 4)}-${s.slice(4, 8)}` : s;
}

export function codeVerificationValide(code: string): boolean {
  return /^[0-9A-Z]{4}-[0-9A-Z]{4}$/.test(code);
}

// ---------------------------------------------------------------- état annoncé par GET /v0/moi

export interface ResumeCles {
  /** peut-on enregistrer une clé depuis cet écran ? */
  peutEnroler: boolean;
  ton: 'ok' | 'attention' | 'info';
  texte: string;
}

interface MoiCles {
  roles: readonly string[];
  /** Tient un rôle du circuit de validation (CV9 : chaque décideur a une clé, même sans rôle d'accès). */
  membre_circuit?: boolean;
  compte_service?: boolean;
  anonyme?: boolean;
  validation_ecritures_motif?: string | null;
  webauthn?: {
    cles_actives: number;
    cles_en_attente: number;
    prochaine_activation?: string | null;
    approbations_en_attente: number;
    peut_approuver: boolean;
    adresse_alerte?: { exigee: boolean; suffisante: boolean } | null;
  } | null;
}

/** Ce que GET /v0/moi dit des clés de la personne, en une phrase (l'application n'invente rien : l'API reste seule juge). */
export function resumeCles(moi: MoiCles, formaterDate: (iso: string) => string): ResumeCles {
  if (moi.anonyme)
    return { peutEnroler: false, ton: 'info', texte: 'Appel anonyme (mode développement sans jeton) : une clé de sécurité exige une personne connectée.' };
  if (moi.compte_service)
    return { peutEnroler: false, ton: 'info', texte: 'Un compte de service (agent, outil) n’a pas de clé de sécurité : seule une personne valide une écriture.' };
  if (!moi.roles.includes('ecriture') && !moi.roles.includes('administration') && !moi.membre_circuit)
    return {
      peutEnroler: false,
      ton: 'info',
      texte: 'Votre rôle ne permet pas de valider des écritures (rôle « écriture » ou « administration » nécessaire) : vous n’avez pas besoin de clé de sécurité.',
    };
  if (moi.validation_ecritures_motif === 'application_non_interactive')
    return { peutEnroler: false, ton: 'attention', texte: TEXTE_APPLICATION_NON_INTERACTIVE };
  if (moi.validation_ecritures_motif === 'applications_humaines_non_declarees')
    return { peutEnroler: false, ton: 'attention', texte: TEXTE_APPLICATIONS_HUMAINES_NON_DECLAREES };
  const w = moi.webauthn;
  if (!w || moi.validation_ecritures_motif === 'webauthn_indisponible')
    return {
      peutEnroler: false,
      ton: 'attention',
      texte: 'La validation par clé de sécurité n’est pas configurée sur cette installation : aucune clé ne peut être enregistrée, aucune écriture validée. Voyez votre administrateur.',
    };
  if (w.cles_actives > 0)
    return {
      peutEnroler: true,
      ton: 'ok',
      texte: `Vous avez ${w.cles_actives} clé${w.cles_actives > 1 ? 's' : ''} active${w.cles_actives > 1 ? 's' : ''} : vous pouvez valider des écritures.${
        w.cles_en_attente ? ` ${w.cles_en_attente} autre${w.cles_en_attente > 1 ? 's' : ''} en attente.` : ''
      }${
        w.prochaine_activation
          ? ` Une clé ajoutée est en délai de sécurité jusqu’au ${formaterDate(w.prochaine_activation)} : d’ici là, seules vos clés déjà actives valident.`
          : ''
      }`,
    };
  if (moi.validation_ecritures_motif === 'cle_en_carence' || (w.cles_en_attente > 0 && w.prochaine_activation))
    return {
      peutEnroler: true,
      ton: 'attention',
      texte: `Votre clé est approuvée, mais dans son délai de sécurité${
        w.prochaine_activation ? ` : elle sera active le ${formaterDate(w.prochaine_activation)}` : ''
      }. Si vous n’avez rien demandé, révoquez-la et prévenez votre RSSI.`,
    };
  if (w.cles_en_attente > 0)
    return {
      peutEnroler: true,
      ton: 'attention',
      texte: 'Votre clé attend l’approbation d’un administrateur : lisez-lui le code de vérification affiché ci-dessous, par téléphone ou en personne.',
    };
  return {
    peutEnroler: true,
    ton: 'attention',
    texte: 'Vous n’avez aucune clé de sécurité active : enregistrez-en une pour pouvoir valider des écritures.',
  };
}
